import { ROLE_MATRIX } from './matrix';
import type { RoleDefinition, PermissionRule } from './types';

interface ResolvedRole extends RoleDefinition {
    resolvedGrants: PermissionRule[];
}

const roleCache = new Map<string, ResolvedRole>();
let roleOverrides: Partial<Record<string, PermissionRule[]>> = {};

const getRoleDefinition = (roleKey: string): RoleDefinition | null => {
    const base = ROLE_MATRIX[roleKey];
    if (!base) return null;

    const overrideGrants = roleOverrides[roleKey];
    if (!overrideGrants) {
        return base;
    }

    return {
        ...base,
        grants: overrideGrants,
    };
};

const clearCaches = () => {
    roleCache.clear();
};

const resolveRole = (roleKey: string, visited: Set<string> = new Set()): ResolvedRole | null => {
    if (roleCache.has(roleKey)) {
        return roleCache.get(roleKey)!;
    }

    const definition = getRoleDefinition(roleKey);
    if (!definition) {
        return null;
    }

    if (visited.has(roleKey)) {
        throw new Error(`Circular role inheritance detected for role "${roleKey}"`);
    }
    visited.add(roleKey);

    let grants: PermissionRule[] = [...(definition.grants || [])];
    for (const inheritedKey of definition.inherits || []) {
        const inherited = resolveRole(inheritedKey, new Set(visited));
        if (inherited) {
            grants = grants.concat(inherited.resolvedGrants);
        }
    }

    const resolved: ResolvedRole = {
        ...definition,
        resolvedGrants: grants,
    };

    roleCache.set(roleKey, resolved);
    return resolved;
};

export const getResolvedRole = (roleKey: string): ResolvedRole | null => {
    return resolveRole(roleKey) ?? null;
};

export const getResolvedRoles = (roleKeys: string[]): ResolvedRole[] => {
    return roleKeys
        .map((key) => getResolvedRole(key))
        .filter((role): role is ResolvedRole => !!role);
};

export const setRoleGrantOverrides = (overrides: Partial<Record<string, PermissionRule[]>>): void => {
    roleOverrides = overrides;
    clearCaches();
};

export const upsertRoleGrantOverride = (roleKey: string, grants: PermissionRule[]): void => {
    roleOverrides = {
        ...roleOverrides,
        [roleKey]: grants,
    };
    clearCaches();
};

export const getRoleGrantOverrides = (): Partial<Record<string, PermissionRule[]>> => {
    return roleOverrides;
};
